import React, { useState, useEffect } from "react";
import { dummyStudents } from "../../data/dummyStudents";
import { tuitionConfigs } from "../../data/tuitionConfigs";
import { getCurrentUser } from "../../auth";

export default function Reports() {
  const [payments, setPayments] = useState(() => JSON.parse(localStorage.getItem("payments")) || []);
  const currentUser = getCurrentUser();
  const userCampus = String(currentUser?.campus || "").toLowerCase();
  const isAdmin = currentUser?.role === "admin" || currentUser?.role === "superadmin" || userCampus === "all";
  const scopedPayments = isAdmin ? payments : payments.filter(p => String(p.campus || "").toLowerCase() === userCampus);
  const scopedStudents = isAdmin ? dummyStudents : dummyStudents.filter(s => String(s.campus || "").toLowerCase() === userCampus);

  // Calculate total collected
  const totalCollected = scopedPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);

  // Calculate outstanding per student
  const outstandingList = scopedStudents.map(student => {
    const campusConfig = tuitionConfigs[student.campus];
    const gradeMatch = student.level.match(/\d+/);
    const gradeNumber = gradeMatch ? parseInt(gradeMatch[0], 10) : null;
    const levelConfig = campusConfig.levels.find(lvl => {
      if (!gradeNumber) return false;

      // Match for "Grades 7-10" or "Grade 7-10"
      const rangeMatch = lvl.name.match(/Grade[s]? (\d+)\s*-\s*Grade[s]? (\d+)/i);
      if (rangeMatch) {
        const min = parseInt(rangeMatch[1], 10);
        const max = parseInt(rangeMatch[2], 10);
        return gradeNumber >= min && gradeNumber <= max;
      }

      // Match for "Pre-Elem to Grade 6"
      const preElemMatch = lvl.name.match(/Pre-Elem to Grade (\d+)/i);
      if (preElemMatch) {
        const max = parseInt(preElemMatch[1], 10);
        return gradeNumber && gradeNumber <= max;
      }

      // Match for single grade like "Grade 7"
      const singleMatch = lvl.name.match(/Grade (\d+)/i);
      if (singleMatch) {
        const grade = parseInt(singleMatch[1], 10);
        return gradeNumber === grade;
      }

      // For levels like "Nursery", "Kinder", etc.
      return lvl.name.toLowerCase().includes(student.level.toLowerCase());
    });
    const paymentMode = levelConfig?.paymentModes.find(pm => pm.mode === student.paymentMode);
    const totalDue = paymentMode
      ? paymentMode.schedule.reduce((sum, sched) => sum + (sched.total || (sched.misc || 0) + (sched.tuition || 0)), 0)
      : 0;
    // Calculate total paid for this student
    const studentPayments = scopedPayments.filter(p => p.studentId === student.id);
    const totalPaid = studentPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);

    return {
      student,
      totalDue,
      totalPaid,
      outstanding: totalDue - totalPaid
    };
  });

  const totalOutstanding = outstandingList.reduce((sum, s) => sum + s.outstanding, 0);

  // Collections by campus
  const campuses = [...new Set(scopedStudents.map(s => s.campus))];
  const campusCollections = campuses.map(campus => {
    const campusStudents = scopedStudents.filter(s => s.campus === campus);
    const campusStudentIds = campusStudents.map(s => s.id);
    const campusPayments = scopedPayments.filter(p => campusStudentIds.includes(p.studentId));
    const collected = campusPayments.reduce((sum, p) => sum + Number(p.amount || 0), 0);
    return { campus, collected };
  });

  return (
    <div className="container mt-4">
      <h2>Reports</h2>
      <div className="mb-3">
        <strong>Total Collected:</strong> ₱{totalCollected.toLocaleString()}
      </div>
      <div className="mb-3">
        <strong>Collections by Campus:</strong>
        <ul>
          {campusCollections.map((c, idx) => (
            <React.Fragment key={c.campus}>
              <li>
                {c.campus.charAt(0).toUpperCase() + c.campus.slice(1)}: ₱{c.collected.toLocaleString()}
              </li>
              {/* Show total collected after the first campus */}
              {idx === 0 && (
                <li style={{ listStyle: "none", marginLeft: 0 }}>
                  <strong>Total Collected:</strong> ₱{totalCollected.toLocaleString()}
                </li>
              )}
            </React.Fragment>
          ))}
        </ul>
      </div>
      <div className="mb-3">
        <strong>Total Outstanding:</strong> ₱{totalOutstanding.toLocaleString()}
      </div>
      <div className="mb-3">
        <strong>Outstanding Balances Per Student:</strong>
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>Name</th>
              <th>Campus</th>
              <th>Level</th>
              <th>Total Due</th>
              <th>Total Paid</th>
              <th>Outstanding</th>
            </tr>
          </thead>
          <tbody>
            {outstandingList.map(({ student, totalDue, totalPaid, outstanding }) => (
              <tr key={student.id}>
                <td>{student.name}</td>
                <td>{student.campus}</td>
                <td>{student.level}</td>
                <td>₱{totalDue.toLocaleString()}</td>
                <td>₱{totalPaid.toLocaleString()}</td>
                <td style={{ color: outstanding > 0 ? "red" : "green" }}>
                  ₱{outstanding.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
